// src/components/usuario/CalendarioUsuario.jsx - Vista de solo lectura para usuarios
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getStartOfWeekCalendar,
  toLocalDateTimeString,
  parseDateTimeFlexible,
  getLiteralHours,
  getLiteralDateComponents
} from "../../../../utils/DateHelpers";
import axios from "axios";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId } from "../../../../utils/SessionUser";
import { ENDPOINTS as SEC_ENDPOINTS } from "../../../Seguridad/Utils/Endpoints";
import { personasNormalize } from "../../../General/Objetos/PersonasModel";

const START_HOUR = 0; // Medianoche  
const END_HOUR = 24; // Hasta medianoche del siguiente día
const HOURS = Array.from(
  { length: END_HOUR - START_HOUR },
  (_, i) => START_HOUR + i
);
const MIN_CELL_HEIGHT = 100; // Altura mínima celda mes
const HOUR_BLOCK_PX = 32; // px altura por bloque hora semana

const CATEGORIES = [
  { value: "Corporativo", label: "Corporativo" },
  { value: "Capacitación", label: "Capacitación" },
  { value: "RH", label: "Recursos Humanos" },
  { value: "Finanzas", label: "Finanzas" },
  { value: "Auditoría", label: "Auditoría" },
  { value: "Cumpleaños", label: "Cumpleaños" },
  { value: "Festivo", label: "Días Festivos" },
];

const CATEGORY_CLASS = {
  Corporativo: "corporativo",
  Capacitación: "capacitacion",
  RH: "rh",
  Finanzas: "finanzas",
  Auditoría: "auditoria",
  Cumpleaños: "cumpleanos",
  Festivo: "festivo",
};

const dayNamesShort = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const formatRangeWeekTitle = (startDate) => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);

  const opts = { month: "long", day: "numeric" };
  const startStr = startDate.toLocaleDateString("es-MX", opts);
  const endStr = endDate.toLocaleDateString("es-MX", opts);
  const year = startDate.getFullYear();

  return `${startStr} â€“ ${endStr} ${year}`;
};

const formatMonthTitle = (anchorDate) => {
  const opts = { month: "long", year: "numeric" };
  return anchorDate.toLocaleDateString("es-MX", opts);
};

// === COMPONENTE ===

const CalendarioUsuario = ({ onVolver }) => {
  const [eventos, setEventos] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Vista: "week" | "month"
  const [viewMode, setViewMode] = useState("month");

  // Fecha ancla para navegación (mes/semana)
  const [anchorDate, setAnchorDate] = useState(() => new Date());

  const [selectedEventId, setSelectedEventId] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState("Todas");

  const location = useLocation();

  // Deep linking handler
  useEffect(() => {
    if (location.state && location.state.initialItem) {
      const item = location.state.initialItem;
      // Tratar de obtener fecha
      const fechaStr = item.fechaInicio || item.fecha;
      if (fechaStr) {
        const d = parseDateTimeFlexible(fechaStr);
        if (d) {
          setAnchorDate(d);
          setViewMode('week');
          // Si trae ID, lo seleccionamos
          if (item.id || item.idEvento) {
            setSelectedEventId(item.id || item.idEvento);
          }
        }
      }

      // Limpiar el estado para no re-ejecutar al cambiar viewMode internamente
      // (Optional cleanup or handling logic could go here)
    }
  }, [location.state]);

  const [formData, setFormData] = useState(() => {
    const now = new Date();
    const fin = new Date(now.getTime() + 60 * 60 * 1000);
    return {
      titulo: "",
      descripcion: "",
      categoria: "Corporativo",
      ubicacion: "",
      responsable: "",
      fechaInicio: toLocalDateTimeString(now),
      fechaFin: toLocalDateTimeString(fin),
    };
  });

  const [errores, setErrores] = useState({});

  // === HELPERS MOVIDOS AL INICIO PARA EVITAR REFERENCE ERROR ===

  const getCategoriaClass = (categoria) => {
    if (!categoria) return "corporativo";
    const cat = categoria.toString().trim().toLowerCase();

    if (cat.includes("capacita")) return "capacitacion";
    if (cat === "rh" || cat.includes("recursos")) return "rh";
    if (cat.includes("finanza")) return "finanzas";
    if (cat.includes("auditor")) return "auditoria";
    if (cat.includes("cumple") || cat.includes("birthday")) return "cumpleanos";
    if (cat.includes("festiv") || cat.includes("holiday")) return "festivo";

    return "corporativo";
  };

  const getCategoriaStyles = (catName) => {
    const cls = getCategoriaClass(catName);
    switch (cls) {
      case 'corporativo': return { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' };
      case 'capacitacion': return { bg: '#FFEDD5', text: '#9A3412', border: '#F97316' };
      case 'rh': return { bg: '#F3E8FF', text: '#6B21A8', border: '#8B5CF6' };
      case 'finanzas': return { bg: '#DCFCE7', text: '#166534', border: '#10B981' };
      case 'auditoria': return { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' };
      case 'cumpleanos': return { bg: '#FCE7F3', text: '#9D174D', border: '#F472B6' };
      case 'festivo': return { bg: '#CCFBF1', text: '#0F766E', border: '#14B8A6' };
      default: return { bg: '#F1F5F9', text: '#475569', border: '#94A3B8' };
    }
  };

  const HOUR_BLOCK_PX = 46;

  const getEventStyle = (ev) => {
    const inicio = parseDateTimeFlexible(ev.fechaInicio);
    const fin = parseDateTimeFlexible(ev.fechaFin);
    if (!inicio || !fin) return { top: 0, height: HOUR_BLOCK_PX };

    const startHour = getLiteralHours(ev.fechaInicio);
    const endHour = getLiteralHours(ev.fechaFin);
    const top = (startHour - START_HOUR) * HOUR_BLOCK_PX;
    const height = Math.max(
      (endHour - startHour) * HOUR_BLOCK_PX,
      HOUR_BLOCK_PX / 2
    );

    return {
      top: `${top}px`,
      height: `${height}px`,
    };
  };

  const formatHourLabel = (hour) => {
    const suffix = hour < 12 ? "AM" : "PM";
    const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h} ${suffix}`;
  };

  const isSameDay = (d1, d2) => {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  // === CARGA / GUARDADO PERSISTENTE ===

  const MAP_DB_TO_FRONT = {
    "Recursos Humanos": "RH",
    "Finanzas": "Finanzas",
    "Auditoría": "Auditoría",
    "Cumpleaños": "Cumpleaños",
    "Festivo": "Festivo",
    "Capacitación": "Capacitación",
    "Corporativo": "Corporativo",
    "RH": "RH",
    "Días Festivos": "Festivo"
  };

  const [dbCategorias, setDbCategorias] = useState([]); // [{ idCategoria, nombre, ... }]

  // Cargar CategorÃ­as y Eventos
  // Cargar Categorías, Eventos y Cumpleaños
  useEffect(() => {
    const cargarTodo = async () => {
      try {
        const idUsr = getSessionUserId();

        // 1. CARGA PARALELA DE TODO
        const [resCat, resEv, resPersonas, resUsuarios, resEmpleados, resUnidades] = await Promise.all([
          axios.get(ENDPOINTS.categorias).catch(() => ({ data: [] })),
          axios.get(ENDPOINTS.eventosDashboardByUsuarioId(idUsr)).catch(() => ({ data: [] })),
          axios.get(ENDPOINTS.personas),
          axios.get(SEC_ENDPOINTS.usuarios),
          axios.get(SEC_ENDPOINTS.empleados),
          axios.get(SEC_ENDPOINTS.unidadesOrgEmpleado)
        ]);

        // A. CATEGORÍAS
        const cats = resCat.data || [];
        setDbCategorias(cats);

        // B. EVENTOS — NombreCategoria ya viene resuelto del SP con JOIN
        const rawEventos = resEv.data || [];
        const eventosHydrated = rawEventos.map(e => {
          // NombreCategoria ya viene del SP. Fallback: buscar en cats por ID
          let catName = e.nombreCategoria || e.NombreCategoria;
          if (!catName) {
            const eCatId = e.idCategoria || e.IdCategoria;
            const catObj = cats.find(c => (c.idCategoria || c.IdCategoria) === eCatId);
            catName = catObj ? (catObj.nombre || catObj.Nombre) : "Corporativo";
          }
          // Normalización para UI
          catName = MAP_DB_TO_FRONT[catName] || catName;

          return {
            id: e.idEvento || e.IdEvento,
            titulo: e.titulo || e.Titulo,
            descripcion: e.descripcion || e.Descripcion,
            categoria: catName,
            ubicacion: e.ubicacion || e.Ubicacion,
            responsable: e.responsable || e.Responsable,
            fechaInicio: e.fechaInicio || e.FechaInicio,
            fechaFin: e.fechaFin || e.FechaFin
          };
        });

        // C. CUMPLEAÃ‘OS (Seguridad + GeneraciÃ³n)
        const listaUsuarios = resUsuarios.data || [];
        const listaEmpleados = resEmpleados.data || [];
        const listaUnidades = resUnidades.data || [];
        const listaPersonas = (resPersonas.data || []).map(p => personasNormalize(p));

        // C.1 Mis Unidades
        let miUnidadIds = new Set();
        const miUsuario = listaUsuarios.find(u => String(u.idUsuario) === String(idUsr));
        if (miUsuario) {
          const miEmpleado = listaEmpleados.find(e => e.idPersona === miUsuario.idPersona);
          if (miEmpleado) {
            listaUnidades
              .filter(u => u.idEmpleado === miEmpleado.idEmpleado)
              .forEach(u => miUnidadIds.add(u.idUnidadOrganizacional));
          }
        }

        // C.2 Mapas de Seguridad
        const empMap = {};
        listaUnidades.forEach(u => {
          if (!empMap[u.idEmpleado]) empMap[u.idEmpleado] = [];
          empMap[u.idEmpleado].push(u.idUnidadOrganizacional);
        });

        const personaUnidadMap = {};
        listaEmpleados.forEach(e => {
          if (empMap[e.idEmpleado]) personaUnidadMap[e.idPersona] = new Set(empMap[e.idEmpleado]);
        });

        const checkIntersection = (targetSet) => {
          if (miUnidadIds.size === 0 || !targetSet || targetSet.size === 0) return false;
          for (let uId of targetSet) {
            if (miUnidadIds.has(uId)) return true;
          }
          return false;
        };

        // C.3 Generar Eventos de Cumpleaños
        const hoy = new Date();
        const yearCurrent = hoy.getFullYear();
        const yearNext = yearCurrent + 1;
        const yearsToGen = [yearCurrent, yearNext];

        const cumpleanosEvents = [];

        listaPersonas.forEach(p => {
          if (!p.fechaNacimiento) return;
          // Solo mostrar si compartimos unidad (o si no hay restricción, pero aquí aplicamos la misma que el dashboard)
          if (!checkIntersection(personaUnidadMap[p.idPersona])) return;

          const fechaNacParts = new Date(p.fechaNacimiento);
          const mes = fechaNacParts.getUTCMonth();
          const dia = fechaNacParts.getUTCDate();

          yearsToGen.forEach(y => {
            // Crear fecha: 9 AM
            const fechaCumple = new Date(y, mes, dia, 9, 0, 0);
            const fechaFin = new Date(y, mes, dia, 11, 0, 0);

            cumpleanosEvents.push({
              id: `bday-${p.idPersona}-${y}`,
              titulo: `🎂 Cumpleaños de ${p.nombre} ${p.apellidoPaterno || ''} ${p.apellidoMaterno || ''}`.trim(),
              descripcion: `¡Hoy es el cumpleaños de ${p.nombre} ${p.apellidoPaterno || ''} ${p.apellidoMaterno || ''}! Envíale una felicitación.`.replace(/\s+/g, ' ').trim(),
              categoria: "Cumpleaños",
              ubicacion: "Oficina / Remoto",
              responsable: "Capital Humano",
              fechaInicio: fechaCumple.toISOString(),
              fechaFin: fechaFin.toISOString()
            });
          });
        });

        // D. MERGE Y UPDATE STATE
        const todosEventos = [...eventosHydrated, ...cumpleanosEvents];
        setEventos(todosEventos);
        setDataLoaded(true);

      } catch (error) {
        console.error("Error cargando calendario:", error);
      }
    };

    cargarTodo();
  }, []);

  // === HELPERS DE FECHAS ===

  const weekStart = useMemo(
    () => getStartOfWeekCalendar(anchorDate),
    [anchorDate]
  );

  const daysOfWeek = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  const monthMatrix = useMemo(() => {
    const year = anchorDate.getFullYear();
    const month = anchorDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startGrid = getStartOfWeekCalendar(firstOfMonth);

    const weeks = [];
    let current = new Date(startGrid);

    // Siempre pintamos 6 semanas (6 filas)
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [anchorDate]);

  const eventosSemana = useMemo(() => {
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 7);
    end.setHours(0, 0, 0, 0);

    const filtrados = filtroCategoria === "Todas"
      ? eventos
      : eventos.filter(ev => getCategoriaClass(ev.categoria) === getCategoriaClass(filtroCategoria));

    return filtrados.filter((ev) => {
      const comps = getLiteralDateComponents(ev.fechaInicio);
      if (!comps) return false;
      const d = new Date(comps.year, comps.month - 1, comps.day);
      return d >= start && d < end;
    });
  }, [eventos, weekStart, filtroCategoria]);

  const eventosPorDiaMes = useMemo(() => {
    const map = new Map(); // key: YYYY-MM-DD, value: array de eventos

    const filtrados = filtroCategoria === "Todas"
      ? eventos
      : eventos.filter(ev => getCategoriaClass(ev.categoria) === getCategoriaClass(filtroCategoria));

    filtrados.forEach((ev) => {
      const comps = getLiteralDateComponents(ev.fechaInicio);
      if (!comps) return;
      const key = `${comps.year}-${comps.month.toString().padStart(2, "0")}-${comps.day.toString().padStart(2, "0")}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });

    // orden visual opcional
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const da = parseDateTimeFlexible(a.fechaInicio) || new Date();
        const db = parseDateTimeFlexible(b.fechaInicio) || new Date();
        return da - db;
      });
    }

    return map;
  }, [eventos, filtroCategoria]);

  // === NAV ===

  const goToToday = () => {
    const now = new Date();
    setAnchorDate(now);
  };

  const changePeriod = (delta) => {
    setAnchorDate((prev) => {
      const d = new Date(prev);
      if (viewMode === "week") {
        d.setDate(d.getDate() + delta * 7);
      } else {
        d.setMonth(d.getMonth() + delta);
      }
      return d;
    });
  };

  const handleChangeViewMode = (mode) => {
    setViewMode(mode);
  };

  const handleSelectDayFromMonth = (dayDate) => {
    setAnchorDate(dayDate);
    setViewMode("week");
  };

  // === FORM ===

  const resetFormToNewEvent = () => {
    const base = new Date(anchorDate);
    const start = new Date(base);
    start.setHours(START_HOUR, 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);

    setSelectedEventId(null);
    setFormData({
      titulo: "",
      descripcion: "",
      categoria: "Corporativo",
      ubicacion: "",
      responsable: "",
      fechaInicio: toLocalDateTimeString(start),
      fechaFin: toLocalDateTimeString(end),
    });
    setErrores({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validarFormulario = () => {
    const nuevo = {};
    if (!formData.titulo.trim()) {
      nuevo.titulo = "El título es obligatorio.";
    }
    if (!formData.fechaInicio) {
      nuevo.fechaInicio = "La fecha/hora de inicio es obligatoria.";
    }
    if (!formData.fechaFin) {
      nuevo.fechaFin = "La fecha/hora de fin es obligatoria.";
    }

    const ini = parseDateTimeFlexible(formData.fechaInicio);
    const fin = parseDateTimeFlexible(formData.fechaFin);
    if (ini && fin && fin <= ini) {
      nuevo.fechaFin =
        "La fecha/hora de fin debe ser posterior a la de inicio.";
    }

    setErrores(nuevo);
    return Object.keys(nuevo).length === 0;
  };

  const handleGuardarEvento = (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;

    if (selectedEventId) {
      // Actualizar existente
      setEventos((prev) =>
        prev.map((ev) =>
          ev.id === selectedEventId
            ? {
              ...ev,
              ...formData,
              id: selectedEventId,
            }
            : ev
        )
      );
    } else {
      // Nuevo
      const nuevo = {
        ...formData,
        id: Date.now(),
      };
      setEventos((prev) => [...prev, nuevo]);
    }

    // Después de guardar, limpiamos y dejamos listo para nuevo evento
    resetFormToNewEvent();
  };

  const handleSeleccionarEvento = (evento) => {
    setSelectedEventId(evento.id);
    setFormData({
      titulo: evento.titulo,
      descripcion: evento.descripcion,
      categoria: evento.categoria,
      ubicacion: evento.ubicacion,
      responsable: evento.responsable,
      fechaInicio: toLocalDateTimeString(parseDateTimeFlexible(evento.fechaInicio)),
      fechaFin: toLocalDateTimeString(parseDateTimeFlexible(evento.fechaFin)),
    });
    setErrores({});
  };

  const handleEliminarEvento = (id) => {
    setEventos((prev) => prev.filter((ev) => ev.id !== id));
    if (selectedEventId === id) {
      resetFormToNewEvent();
    }
  };

  const handleCrearEventoDesdeCelda = (dayDate, hour) => {
    const inicio = new Date(dayDate);
    inicio.setHours(hour, 0, 0, 0);
    const fin = new Date(inicio.getTime() + 60 * 60 * 1000);

    setSelectedEventId(null);
    setFormData({
      titulo: "",
      descripcion: "",
      categoria: "Corporativo",
      ubicacion: "",
      responsable: "",
      fechaInicio: toLocalDateTimeString(inicio),
      fechaFin: toLocalDateTimeString(fin),
    });
    setErrores({});
  };



  // === RENDER ===

  return (
    <div className="intra-calendar-page">
      {/* HEADER */}
      <header className="intra-calendar-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1.5rem' }}>
        {/* FILA 1: TÃTULO Y BOTÃ“N VOLVER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {onVolver && (
              <button
                type="button"
                onClick={onVolver}
                style={{
                  marginBottom: '0.75rem',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  color: '#334155',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  transition: 'all 0.2s ease',
                  width: 'fit-content'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.color = '#334155';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <span style={{ fontSize: '1.2rem', lineHeight: '1' }}>←</span>
                <span>Volver al dashboard</span>
              </button>
            )}
            <h1 className="intra-calendar-title" style={{ margin: 0 }}>Calendario corporativo</h1>
          </div>
        </div>

        {/* FILA 2: CONTROLES (TOGGLE, FILTRO, NAV) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2rem' }}>

          {/* Toggle View Mode */}
          <div
            onClick={() => handleChangeViewMode(viewMode === 'week' ? 'month' : 'week')}
            style={{
              position: 'relative',
              display: 'flex',
              backgroundColor: '#f1f5f9',
              borderRadius: '999px',
              padding: '4px',
              border: '1px solid #e2e8f0',
              height: 'fit-content',
              cursor: 'pointer',
              userSelect: 'none',
              width: '160px',
              flexShrink: 0
            }}
          >
            <div style={{
              position: 'absolute',
              top: '4px',
              bottom: '4px',
              left: '4px',
              width: 'calc(50% - 4px)',
              backgroundColor: '#ffffff',
              borderRadius: '999px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              transform: viewMode === 'week' ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
            }} />
            <div style={{ zIndex: 1, flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '0.85rem', fontWeight: viewMode === 'week' ? 600 : 500, color: viewMode === 'week' ? 'var(--color-primary)' : '#64748b', transition: 'color 0.2s' }}>Semana</div>
            <div style={{ zIndex: 1, flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '0.85rem', fontWeight: viewMode === 'month' ? 600 : 500, color: viewMode === 'month' ? 'var(--color-primary)' : '#64748b', transition: 'color 0.2s' }}>Mes</div>
          </div>

          {/* FILTRO CATEGORÃA */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0 2px',
            flex: 1,
            justifyContent: 'center'
          }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              Filtrar por:
            </label>
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="intra-select"
              style={{
                width: '100%',
                maxWidth: '220px',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: 600,
                backgroundColor: filtroCategoria === "Todas" ? '#fff' : getCategoriaStyles(filtroCategoria).bg,
                color: filtroCategoria === "Todas" ? '#475569' : getCategoriaStyles(filtroCategoria).text,
                borderLeft: '4px solid',
                borderLeftColor: filtroCategoria === "Todas" ? '#cbd5e1' : getCategoriaStyles(filtroCategoria).border,
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                borderRadius: '8px'
              }}
            >
              <option value="Todas">Todas las categorías</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* NAV CONTROLS - DERECHA */}
          <div className="intra-calendar-header-controls" style={{ flexShrink: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="intra-calendar-nav" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                <button
                  type="button"
                  className="intra-button-ghost"
                  onClick={() => changePeriod(-1)}
                  style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="intra-button-ghost"
                  onClick={goToToday}
                  style={{ fontSize: '0.85rem', fontWeight: 600, padding: '4px 12px', height: '28px' }}
                >
                  Hoy
                </button>
                <button
                  type="button"
                  className="intra-button-ghost"
                  onClick={() => changePeriod(1)}
                  style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                >
                  ›
                </button>
              </div>
              <p className="intra-calendar-range" style={{ margin: 0, fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                {viewMode === "week"
                  ? formatRangeWeekTitle(weekStart)
                  : formatMonthTitle(anchorDate)}
              </p>
            </div>
          </div>

        </div>
      </header>

      {/* LAYOUT PRINCIPAL */}
      <section className="intra-calendar-layout">
        {/* GRID DEL CALENDARIO */}
        <div className="intra-calendar-grid">
          {viewMode === "week" ? (
            <>
              {/* Cuerpo semana con header sincronizado */}
              <div className="intra-calendar-week-wrapper">
                {/* FILA 1: Header con spacer + dias (AZUL - NO SCROLL) */}
                <div className="intra-calendar-week-header-row">
                  {/* Spacer izquierdo para alinear con columna de horas */}
                  <div className="intra-calendar-hours-header-spacer"></div>

                  {/* Header de días - scrolleable horizontalmente pero fijo verticalmente */}
                  <div className="intra-calendar-days-header-wrapper">
                    <div className="intra-calendar-days-header">
                      {daysOfWeek.map((day, idx) => (
                        <div key={idx} className="intra-calendar-day-header">
                          <span className="intra-calendar-day-name">
                            {dayNamesShort[day.getDay()]}
                          </span>
                          <span className="intra-calendar-day-number">
                            {day.getDate()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* FILA 2: Contenido scrolleable - horas + eventos (ROJO - SI SCROLL) */}
                <div className="intra-calendar-week-body">
                  {/* Columna de horas fija a la izquierda */}
                  <div className="intra-calendar-hours-column">
                    {HOURS.map((h) => (
                      <div key={h} className="intra-calendar-hour-row">
                        <span className="intra-calendar-hour-label">
                          {formatHourLabel(h)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Columnas de eventos - scrolleable */}
                  <div className="intra-calendar-days-body-wrapper">
                    <div className="intra-calendar-days-columns">
                      {daysOfWeek.map((day, dayIndex) => (
                        <div key={dayIndex} className="intra-calendar-day-column">
                          {/* celdas clicables */}
                          {HOURS.map((h) => (
                            <div
                              key={h}
                              className="intra-calendar-cell"
                              onClick={() =>
                                handleCrearEventoDesdeCelda(day, h)
                              }
                            ></div>
                          ))}

                          {/* eventos del dÃ­a */}
                          {eventosSemana
                            .filter((ev) => {
                              const d = parseDateTimeFlexible(ev.fechaInicio);
                              return d && isSameDay(d, day);
                            })
                            .map((ev) => (
                              <div
                                key={ev.id}
                                className={
                                  "intra-calendar-event-block " +
                                  "intra-calendar-event-block--" +
                                  getCategoriaClass(ev.categoria) +
                                  (selectedEventId === ev.id
                                    ? " intra-calendar-event-block--selected"
                                    : "")
                                }
                                style={{
                                  ...getEventStyle(ev),
                                  backgroundColor: getCategoriaStyles(ev.categoria).bg,
                                  borderLeft: `3px solid ${getCategoriaStyles(ev.categoria).border}`,
                                  color: getCategoriaStyles(ev.categoria).text,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  overflow: 'hidden'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSeleccionarEvento(ev);
                                }}
                              >
                                <p className="intra-calendar-event-title" style={{ color: getCategoriaStyles(ev.categoria).text, fontSize: '10px', fontWeight: 700 }}>
                                  {ev.titulo}
                                </p>
                                {ev.ubicacion && (
                                  <p className="intra-calendar-event-location" style={{ color: getCategoriaStyles(ev.categoria).text, opacity: 0.8, fontSize: '9px' }}>
                                    {ev.ubicacion}
                                  </p>
                                )}
                              </div>
                            ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </>
          ) : (
            <>
              {/* Cabecera dÃƒÂ­as mes */}
              <div className="intra-calendar-month-header">
                {dayNamesShort.map((name) => (
                  <div
                    key={name}
                    className="intra-calendar-month-header-cell"
                  >
                    {name.toUpperCase()}
                  </div>
                ))}
              </div>

              {/* Cuerpo mes */}
              <div className="intra-calendar-month-body">
                {monthMatrix.map((week, wIdx) => (
                  <div key={wIdx} className="intra-calendar-month-row">
                    {week.map((day, dIdx) => {
                      const isCurrentMonth =
                        day.getMonth() === anchorDate.getMonth();
                      const key = `${day.getFullYear()}-${(day.getMonth() + 1)
                        .toString()
                        .padStart(2, "0")}-${day
                          .getDate()
                          .toString()
                          .padStart(2, "0")}`;
                      const eventsForDay = eventosPorDiaMes.get(key) || [];

                      const isToday = isSameDay(day, new Date());

                      return (
                        <div
                          key={dIdx}
                          className={
                            "intra-calendar-month-cell" +
                            (isCurrentMonth
                              ? ""
                              : " intra-calendar-month-cell--faded") +
                            (isToday
                              ? " intra-calendar-month-cell--today"
                              : "")
                          }
                          onClick={() => handleSelectDayFromMonth(day)}
                        >
                          <div className="intra-calendar-month-cell-header">
                            <span className="intra-calendar-month-day-number">
                              {day.getDate()}
                            </span>
                          </div>

                          <div className="intra-calendar-month-events">
                            {eventsForDay
                              .sort((a, b) => {
                                const ta = parseDateTimeFlexible(a.fechaInicio);
                                const tb = parseDateTimeFlexible(b.fechaInicio);
                                return ta - tb;
                              })
                              .slice(0, 2)
                              .map((ev) => (
                                <button
                                  key={ev.id}
                                  type="button"
                                  className={
                                    "intra-calendar-month-event-pill " +
                                    "intra-calendar-month-event-pill--" +
                                    getCategoriaClass(ev.categoria)
                                  }
                                  style={{
                                    backgroundColor: getCategoriaStyles(ev.categoria).bg,
                                    color: getCategoriaStyles(ev.categoria).text,
                                    border: 'none',
                                    borderLeft: `3px solid ${getCategoriaStyles(ev.categoria).border}`,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                    borderRadius: '4px',
                                    padding: '3px 8px',
                                    marginBottom: '2px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                    textAlign: 'left',
                                    minHeight: '22px',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSeleccionarEvento(ev);
                                  }}
                                >
                                  <span className="intra-calendar-month-event-text" style={{ 
                                    fontWeight: 700, 
                                    fontSize: '10.5px',
                                    lineHeight: '1.2',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    wordBreak: 'break-word'
                                  }}>
                                    {ev.titulo}
                                  </span>
                                </button>
                              ))}
                            {eventsForDay.length > 2 && (
                               <span className="intra-calendar-month-more" style={{ fontSize: '10px', paddingLeft: '4px', fontWeight: 600, opacity: 0.7 }}>
                                 + {eventsForDay.length - 2} más
                               </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* MODAL: DETALLES DEL EVENTO */}
        {selectedEventId && (() => {
          const evento = eventos.find((e) => e.id === selectedEventId);
          if (!evento) return null;

          return (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
                backdropFilter: "blur(2px)",
              }}
              onClick={() => setSelectedEventId(null)}
            >
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "1rem",
                  padding: "2rem",
                  width: "90%",
                  maxWidth: "1000px",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  position: "relative",
                  maxHeight: "90vh",
                  overflowY: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="intra-calendar-sidepanel-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 className="intra-avisos-section-title" style={{ margin: 0, fontSize: '1.25rem' }}>Detalle de evento</h2>
                  <button
                    type="button"
                    className="intra-button-ghost"
                    onClick={() => setSelectedEventId(null)}
                    style={{ fontSize: '1.5rem', lineHeight: 1, padding: '0.5rem' }}
                  >
                    ×
                  </button>
                </div>

                <div className="intra-calendar-event-details">
                  {/* HEADER: Título y Categoría alineados */}
                  <div className="intra-event-detail-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1.5rem' }}>
                    <h3 className="intra-event-detail-title" style={{ fontSize: '1.8rem', margin: 0, lineHeight: 1.2, color: '#1e293b' }}>
                      {evento.titulo}
                    </h3>
                    <span
                      style={{
                        flexShrink: 0,
                        padding: '0.4rem 1rem',
                        borderRadius: '9999px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        marginLeft: '1rem',
                        height: 'fit-content',
                        backgroundColor: getCategoriaStyles(evento.categoria).bg,
                        color: getCategoriaStyles(evento.categoria).text,
                        border: `1px solid ${getCategoriaStyles(evento.categoria).border}33`
                      }}
                    >
                      {evento.categoria}
                    </span>
                  </div>

                  {/* GRID LAYOUT: Responsivo via CSS */}
                  <div className="intra-calendar-modal-grid">

                    {/* COLUMNA IZQUIERDA: Metadatos (Horario, Responsable, Ubicación) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                      {/* Horario Compacto */}
                      <div>
                        <label className="intra-event-detail-label" style={{ display: 'block', fontWeight: 700, marginBottom: '0.75rem', color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Horario
                        </label>
                        <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '1rem', border: '1px solid #e2e8f0' }}>
                          <div style={{ marginBottom: '1rem' }}>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Inicio</div>
                            <div style={{ fontWeight: 600, color: '#334155', fontSize: '1rem' }}>
                              {evento.fechaInicio
                                ? parseDateTimeFlexible(evento.fechaInicio).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })
                                : "N/A"}
                            </div>
                          </div>
                          <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '1rem' }}>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Fin</div>
                            <div style={{ fontWeight: 600, color: '#334155', fontSize: '1rem' }}>
                              {evento.fechaFin
                                ? parseDateTimeFlexible(evento.fechaFin).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })
                                : "N/A"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Responsable y UbicaciÃ³n unidos */}
                      <div style={{ display: 'grid', gap: '1.25rem' }}>
                        {evento.responsable && (
                          <div>
                            <label className="intra-event-detail-label" style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Responsable</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>👤</div>
                              <p style={{ margin: 0, fontWeight: 500, color: '#334155' }}>{evento.responsable}</p>
                            </div>
                          </div>
                        )}

                        {evento.ubicacion && (
                          <div>
                            <label className="intra-event-detail-label" style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ubicación</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>📍</div>
                              <p style={{ margin: 0, fontWeight: 500, color: '#334155' }}>{evento.ubicacion}</p>
                            </div>
                          </div>
                        )}
                      </div>

                    </div>

                    {/* COLUMNA DERECHA: DescripciÃ³n (Mayor espacio) */}
                    <div>
                      <label className="intra-event-detail-label" style={{ display: 'block', fontWeight: 700, marginBottom: '1rem', color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Descripción del evento
                      </label>
                      <div style={{ color: '#334155', lineHeight: 1.7, fontSize: '1rem', whiteSpace: 'pre-wrap' }}>
                        {evento.descripcion || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin descripción adicional.</span>}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </section>
    </div >
  );
};

export default CalendarioUsuario;

